import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCog, LogOut } from "lucide-react";
import { getUser, getImpersonation, stopImpersonation, isImpersonating } from "@/lib/api";

/**
 * Fixed top banner shown whenever an admin is signed in AS another user.
 * Lets the admin exit impersonation in one click — restoring their admin
 * session from `hn_admin_backup` and returning them to /admin.
 *
 * Renders NOTHING when no impersonation is active, so it's safe to mount
 * globally in <App>.
 */
export default function ImpersonationBanner() {
  const nav = useNavigate();
  const [state, setState] = useState({ active: false, target: null, meta: null });

  const refresh = () => {
    if (!isImpersonating()) {
      setState({ active: false, target: null, meta: null });
      return;
    }
    setState({ active: true, target: getUser(), meta: getImpersonation() });
  };

  useEffect(() => {
    refresh();
    // Refresh on storage changes so opening a second tab keeps banners in sync
    const onStorage = (e) => {
      if (["hn_token", "hn_admin_backup", "hn_impersonation"].includes(e.key)) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!state.active) return null;

  const handleExit = () => {
    stopImpersonation();
    // Full navigation so the AuthContext (and any dashboard queries) re-fetch
    // with the restored admin token.
    window.location.assign("/admin");
    // Fallback if location.assign is intercepted
    setTimeout(() => nav("/admin"), 100);
  };

  const targetName = state.target?.name || state.target?.email || "user";
  const targetRole = state.target?.role || "user";
  const adminEmail = state.meta?.admin_email || "admin";

  return (
    <div
      data-testid="impersonation-banner"
      role="status"
      className="sticky top-0 z-[60] w-full bg-gradient-to-r from-amber-500 via-orange-500 to-vermilion-600 text-white shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <UserCog size={18} className="flex-shrink-0" />
          <span className="truncate">
            You are acting as <b className="font-black">{targetName}</b>
            <span className="hidden sm:inline"> · <span className="capitalize opacity-90">{targetRole}</span></span>
            <span className="hidden md:inline opacity-90"> · admin: {adminEmail}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleExit}
          data-testid="impersonation-exit-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-vermilion-700 hover:bg-slate-100 h-8 px-3 text-xs font-black uppercase tracking-wider shadow active:scale-95 transition-transform"
        >
          <LogOut size={13} /> Exit impersonation
        </button>
      </div>
    </div>
  );
}
